import { useEffect, useRef } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { analysisMessagesInfiniteQueryOptions } from "@/hooks/use-analysis";
import type { AnalysisFilters, AnalysisMessageRow } from "@/types/analysis";

interface Props {
  title: string;
  description?: string;
  filters: AnalysisFilters | null;
  onOpenChange: (open: boolean) => void;
  onOpenSession: (sessionId: string, messageId: string) => void;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString("ko-KR", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function RoleBadge({ role }: { role: string }) {
  const label =
    role === "user" ? "유저" : role === "assistant" ? "AI" : role;
  return (
    <Badge
      variant={role === "user" ? "default" : "secondary"}
      className="shrink-0"
    >
      {label}
    </Badge>
  );
}

function MessageRow({
  row,
  onClick,
}: {
  row: AnalysisMessageRow;
  onClick: () => void;
}) {
  const isNegative = !!row.negativeFeedbackLabel;
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "w-full rounded-md border p-3 text-left focus:outline-none",
        isNegative
          ? "bg-red-500/8 border-red-500/30 hover:bg-red-500/12 focus:bg-red-500/12"
          : "hover:bg-muted/60 focus:bg-muted/60",
      ].join(" ")}
    >
      <div className="mb-2 flex items-center gap-2">
        <RoleBadge role={row.role} />
        {row.intentLabelKo && (
          <Badge variant="outline" className="text-xs">
            {row.intentLabelKo}
          </Badge>
        )}
        {row.negativeFeedbackLabel && (
          <Badge variant="destructive" className="text-xs">
            {row.negativeFeedbackLabel}
          </Badge>
        )}
        {row.positiveFeedbackLabel && (
          <Badge className="bg-emerald-500/15 text-emerald-700 text-xs hover:bg-emerald-500/20">
            {row.positiveFeedbackLabel}
          </Badge>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          {formatTime(row.messageCreatedAt)}
        </span>
      </div>
      <p className="line-clamp-3 text-sm whitespace-pre-wrap">
        {row.contentPreview || "(빈 메시지)"}
      </p>
      {row.contentLength > row.contentPreview.length && (
        <p className="mt-1 text-[11px] text-muted-foreground">
          {row.contentLength.toLocaleString("ko-KR")}자 (미리보기 잘림)
        </p>
      )}
    </button>
  );
}

export function MessagesDrawer({
  title,
  description,
  filters,
  onOpenChange,
  onOpenSession,
}: Props) {
  const open = filters !== null;
  const query = useInfiniteQuery({
    ...analysisMessagesInfiniteQueryOptions(filters ?? {}),
    enabled: open,
  });

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    if (!hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const firstPage = query.data?.pages[0];
  const total = firstPage?.total ?? 0;
  const rows: AnalysisMessageRow[] =
    query.data?.pages.flatMap((page) => page.rows) ?? [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full md:max-w-[min(720px,60vw)] overflow-y-auto px-6 py-6"
      >
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>
            {description ?? "라벨이 적용된 메시지를 조회합니다."}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          {query.isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}

          {query.isError && (
            <div className="rounded-md border p-4 text-sm text-destructive">
              메시지를 불러오지 못했습니다.
              <Button
                size="sm"
                variant="outline"
                className="ml-2"
                onClick={() => query.refetch()}
              >
                다시 시도
              </Button>
            </div>
          )}

          {firstPage && (
            <>
              <p className="text-xs text-muted-foreground">
                총 {total.toLocaleString("ko-KR")}건 중{" "}
                {rows.length.toLocaleString("ko-KR")}건 로드
              </p>
              {rows.length === 0 ? (
                <p className="rounded-md border p-6 text-center text-sm text-muted-foreground">
                  해당 조건의 메시지가 없습니다.
                </p>
              ) : (
                <>
                  {rows.map((row) => (
                    <MessageRow
                      key={row.messageId}
                      row={row}
                      onClick={() =>
                        onOpenSession(row.sessionId, row.messageId)
                      }
                    />
                  ))}
                  <div ref={sentinelRef} aria-hidden className="h-1" />
                  {query.isFetchingNextPage && (
                    <div className="space-y-3">
                      <Skeleton className="h-24 w-full" />
                      <Skeleton className="h-24 w-full" />
                    </div>
                  )}
                  {!query.hasNextPage && rows.length > 0 && (
                    <p className="py-4 text-center text-xs text-muted-foreground">
                      모든 메시지를 불러왔습니다.
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
